/**
 * Product Card Management System - Moon Moon Agency v1.0
 * Handles variant selection and quantity controls for product cards
 * Uses event delegation pattern consistent with mm_main_product.js
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION
 */
const CARD_CONFIG = {
    selectors: {
        cardForm: '[data-type="add-to-cart-form"]',
        variantInput: '[data-card-variant-input]',
        variantIdInput: '[data-variant-id-input]',
        quantityInput: '[data-card-qtty-input]',
        quantityIncrease: '[data-card-qtty-increase]',
        quantityDecrease: '[data-card-qtty-decrease]',
        submitButton: '.mm-product-card-cta',
        productCard: '.mm-product-card',
        panelWrapper: '[data-card-panel-wrapper]',
        panelTrigger: '[data-card-panel-trigger]',
        panelClose: '[data-card-panel-close]'
    },

    classes: {
        panelOpen: 'mm-is-open'
    },

};

/*********************************************************************************************
 * 📦 PRODUCT DATA MANAGEMENT
 */
const ProductCardData = {
    /**
     * Get product data from a specific card element
     * @param {HTMLElement} card - The card element containing the product data
     */
    getFromCard(card) {
        if (!card) {
            window.log('error', 'ProductCard', 'No card element provided');
            return null;
        }

        // Find the script tag within this specific card
        const scriptTag = card.querySelector('script[data-product-id]');
        if (!scriptTag) {
            window.log('error', 'ProductCard', 'No product data script found in card');
            return null;
        }

        try {
            const data = JSON.parse(scriptTag.textContent);

            // Validate required properties
            if (!data || typeof data.id === 'undefined' || !Array.isArray(data.variants)) {
                window.log('error', 'ProductCard', 'Invalid product data structure', { data });
                return null;
            }

            window.log('info', 'ProductCard', 'Product data loaded', {
                productId: data.id,
                variantsCount: data.variants.length
            });
            return data;
        } catch (e) {
            window.log('error', 'ProductCard', 'Failed to parse product data', { error: e.message });
            return null;
        }
    }
};

/*********************************************************************************************
 * 🔄 VARIANT MANAGEMENT
 */
const VariantManager = {
    findMatchingVariant(productData, selectedOptions) {
        if (!productData || !productData.variants) {
            window.log('error', 'ProductCard', 'Invalid product data');
            return null;
        }

        const matchedVariant = productData.variants.find(variant => {
            // Check each option position
            return Object.keys(selectedOptions).every(optionKey => {
                const selectedValue = selectedOptions[optionKey];
                const variantValue = variant[optionKey];
                return variantValue === selectedValue;
            });
        });

        if (!matchedVariant) {
            window.log('warn', 'ProductCard', 'No variant matched', {
                selectedOptions,
                availableVariants: productData.variants.map(v => ({
                    option1: v.option1,
                    option2: v.option2,
                    option3: v.option3
                }))
            });
        }

        return matchedVariant;
    },

    getSelectedOptions(panel) {
        const options = {};
        const inputs = panel.querySelectorAll(CARD_CONFIG.selectors.variantInput);

        window.log('info', 'ProductCard', 'Getting selected options', {
            totalInputs: inputs.length,
            panelId: panel.dataset.cardPanelWrapper
        });

        inputs.forEach(input => {
            if (input.checked) {
                const position = input.dataset.optionPosition;
                const optionKey = `option${position}`;
                options[optionKey] = input.value;

                window.log('info', 'ProductCard', 'Found checked option', {
                    position,
                    optionKey,
                    value: input.value
                });
            }
        });

        return options;
    },

    updateVariantId(form, variantId) {
        const input = form.querySelector(CARD_CONFIG.selectors.variantIdInput);
        if (input) {
            const oldValue = input.value;
            input.value = variantId;
            window.log('info', 'ProductCard', 'Variant ID updated in hidden input', {
                oldValue,
                newValue: variantId,
                inputName: input.name,
                inputId: input.id
            });
        } else {
            window.log('error', 'ProductCard', 'Hidden variant input not found', {
                selector: CARD_CONFIG.selectors.variantIdInput
            });
        }
    },

    updateButton(panel, variant) {
        const button = panel.querySelector(CARD_CONFIG.selectors.submitButton);
        if (!button) return;

        // Use texts from data attributes (set by Liquid translations)
        const addText = button.dataset.addText;
        const soldOutText = button.dataset.soldOutText;

        if (!addText || !soldOutText) {
            window.log('warn', 'ProductCard', 'Button missing translation data attributes', {
                hasAddText: !!addText,
                hasSoldOutText: !!soldOutText
            });
            return;
        }

        const newText = variant.available ? addText : soldOutText;
        const labels = button.querySelectorAll('[data-btn-label]');

        if (variant.available) {
            button.disabled = false;
            button.classList.remove('mm-cursor-default');
        } else {
            button.disabled = true;
            button.classList.add('mm-cursor-default');
        }

        if (labels.length) {
            labels.forEach(label => { label.textContent = newText; });
        } else {
            button.textContent = newText;
        }
    },

    updateVariantImage(form, variant) {
        if (!variant.featured_image) {
            window.log('info', 'ProductCard', 'No featured image for variant', { variantId: variant.id });
            return;
        }

        const card = form.closest(CARD_CONFIG.selectors.productCard);
        if (!card) return;

        // Find the main image in the card (not the hover image)
        const mainImage = card.querySelector('.mm-product-card-image:not(.mm-product-card-image-hover)');
        if (!mainImage) {
            window.log('warn', 'ProductCard', 'Main product image not found in card');
            return;
        }

        try {
            // featured_image is a URL string from the JSON data
            const imageUrl = typeof variant.featured_image === 'string'
                ? variant.featured_image
                : variant.featured_image.src;
            mainImage.src = imageUrl;
            mainImage.alt = variant.name || mainImage.alt;

            // Update srcset if present (for responsive images)
            if (mainImage.hasAttribute('srcset')) {
                // Generate responsive srcset for variant image
                const srcset = this.generateResponsiveSrcset(imageUrl);
                mainImage.srcset = srcset;
            }

            window.log('info', 'ProductCard', 'Variant image updated', {
                variantId: variant.id,
                imageUrl
            });
        } catch (error) {
            window.log('error', 'ProductCard', 'Failed to update variant image', {
                variantId: variant.id,
                error: error.message
            });
        }
    },

    generateResponsiveSrcset(originalUrl) {
        if (!originalUrl || typeof originalUrl !== 'string') {
            return '';
        }

        // Common responsive breakpoints
        const breakpoints = [200, 300, 400, 500, 600, 700, 800, 900, 1000];

        return breakpoints
            .map(width => `${this.getShopifyImageUrl(originalUrl, width)} ${width}w`)
            .join(', ');
    },

    getShopifyImageUrl(originalUrl, width) {
        if (!originalUrl || typeof originalUrl !== 'string') {
            return originalUrl;
        }

        try {
            // Remove any existing size parameters
            let cleanUrl = originalUrl.replace(/_\d+x\d*\./g, '.');

            // Handle URLs with query parameters (like v= version)
            const [baseUrl, queryString] = cleanUrl.split('?');

            // Add width parameter before file extension
            const sizedUrl = baseUrl.replace(/(\.[^.]*$)/, `_${width}x$1`);

            // Reattach query string if it exists
            return queryString ? `${sizedUrl}?${queryString}` : sizedUrl;
        } catch (error) {
            window.log('warn', 'ProductCard', 'Failed to resize image URL, using original', {
                originalUrl,
                error: error.message
            });
            return originalUrl;
        }
    },

    updateQuantityMax(form, variant) {
        const card = form.closest(CARD_CONFIG.selectors.productCard);
        if (!card) return;

        const quantityInput = card.querySelector(CARD_CONFIG.selectors.quantityInput);
        if (!quantityInput) return;

        let maxQty = 9999;

        if (variant.inventory_management &&
            variant.inventory_policy !== 'continue' &&
            variant.inventory_quantity > 0) {
            maxQty = variant.inventory_quantity;
        }

        quantityInput.max = maxQty;
        quantityInput.dataset.variantInventory = maxQty;

        // Reset quantity if it exceeds max
        const currentQty = parseInt(quantityInput.value, 10);
        if (currentQty > maxQty) {
            quantityInput.value = maxQty;
        }

        // Update button states
        QuantityManager.updateButtonStates(quantityInput);
    },

    handleVariantChange(panel) {
        // The panel is inserted into <body> by ProductCardManager._mountPanel(), so it's not
        // a descendant of its product card/form — resolve those via the shared card id instead
        // of DOM traversal (closest()/querySelector() would fail across the teleported subtree).
        const cardId = panel.dataset.cardPanelWrapper;
        const card = document.querySelector(`${CARD_CONFIG.selectors.productCard}[data-card-id="${cardId}"]`);
        if (!card) {
            window.log('error', 'ProductCard', 'Cannot find product card for panel', { cardId });
            return;
        }

        const form = card.querySelector(CARD_CONFIG.selectors.cardForm);
        if (!form) {
            window.log('error', 'ProductCard', 'Cannot find form for card', { cardId });
            return;
        }

        // Get product data from this specific card
        const productData = ProductCardData.getFromCard(card);
        if (!productData) {
            window.log('warn', 'ProductCard', 'No product data for variant change');
            return;
        }

        const selectedOptions = this.getSelectedOptions(panel);
        const matchedVariant = this.findMatchingVariant(productData, selectedOptions);

        if (matchedVariant) {
            this.updateVariantId(form, matchedVariant.id);
            this.updateButton(panel, matchedVariant);
            this.updateQuantityMax(form, matchedVariant);
            this.updateVariantImage(form, matchedVariant);

            window.log('info', 'ProductCard', 'Variant changed successfully', {
                productId: productData.id,
                variantId: matchedVariant.id,
                available: matchedVariant.available,
                selectedOptions
            });
        } else {
            window.log('warn', 'ProductCard', 'No variant matched', {
                productId: productData.id,
                selectedOptions,
                availableVariantsCount: productData.variants.length
            });

            // Disable button if no variant matches
            this.updateButton(panel, { available: false });
        }
    }
};

/*********************************************************************************************
 * 🔢 QUANTITY MANAGEMENT
 */
const QuantityManager = {
    updateButtonStates(input) {
        if (!input) return;

        const currentValue = parseInt(input.value, 10) || 1;
        const maxValue = parseInt(input.max, 10) || 9999;

        const card = input.closest(CARD_CONFIG.selectors.productCard);
        if (!card) return;

        // Find plus button within the same card
        const plusBtn = card.querySelector(CARD_CONFIG.selectors.quantityIncrease);

        // Update plus button state only (minus button always enabled)
        if (plusBtn) {
            if (currentValue >= maxValue) {
                plusBtn.setAttribute('disabled', '');
            } else {
                plusBtn.removeAttribute('disabled');
            }
        }
    },

    changeQuantity(delta, button) {
        // Find the card container first, then find input within that card
        const card = button.closest(CARD_CONFIG.selectors.productCard);
        if (!card) {
            window.log('warn', 'ProductCard', 'Product card not found', { button });
            return;
        }

        const input = card.querySelector(CARD_CONFIG.selectors.quantityInput);
        if (!input) {
            window.log('warn', 'ProductCard', 'Quantity input not found in card', { card });
            return;
        }

        const current = parseInt(input.value, 10) || 1;
        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10) || 9999;

        // Calculate new value with constraints (same logic as mm_main_product.js)
        const newValue = Math.max(min, Math.min(max, current + delta));

        if (newValue !== current) {
            input.value = newValue;

            // Dispatch change event for potential listeners (consistent with mm_main_product.js)
            input.dispatchEvent(new Event('change', { bubbles: true }));

            this.updateButtonStates(input);

            window.log('info', 'ProductCard', 'Quantity changed', {
                oldValue: current,
                newValue,
                delta
            });
        }
    },

    validateInput(input) {
        if (!input) return;

        let value = parseInt(input.value, 10);
        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10) || 9999;

        if (isNaN(value) || value < min) {
            value = min;
        } else if (value > max) {
            value = max;
        }

        input.value = value;
        this.updateButtonStates(input);
    }
};

/*********************************************************************************************
 * 🎬 EVENT DELEGATION & INITIALIZATION
 */
const ProductCardManager = {
    initialized: false,
    _activePanel: null,
    _activeTrigger: null,
    _previousFocus: null,

    // Size panels ship inert inside a <template> in each card (see mm-card-product.liquid) and are only
    // inserted into <body> on first open — body level so Splide's transform doesn't trap position:fixed.
    // Returns the existing panel on later opens so the selected variant is kept.
    _mountPanel(panelId, trigger) {
        const existing = document.querySelector(`[data-card-panel-wrapper="${panelId}"]`);
        if (existing) return { wrapper: existing, isNew: false };

        const template = trigger.closest(CARD_CONFIG.selectors.productCard)
            ?.querySelector(`template[data-card-panel-template="${panelId}"]`);
        if (!template) {
            window.log('error', 'ProductCard', 'Size panel template not found', { panelId });
            return { wrapper: null, isNew: false };
        }

        document.body.appendChild(template.content.cloneNode(true));
        const wrapper = document.querySelector(`[data-card-panel-wrapper="${panelId}"]`);

        // Reassurance slider and accelerated checkout button were inert in the template
        window.SplideManager?.init();
        window.Shopify?.PaymentButton?.init?.();

        return { wrapper, isNew: true };
    },

    openPanel(wrapper, trigger, isNew = false) {
        this._previousFocus = trigger || document.activeElement;
        this._activePanel = wrapper;
        this._activeTrigger = trigger || null;

        // A freshly inserted panel needs its closed state laid out first, otherwise the slide-in transition is skipped
        if (isNew) void wrapper.offsetWidth;

        wrapper.classList.add(CARD_CONFIG.classes.panelOpen);
        wrapper.setAttribute('aria-hidden', 'false');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('stop-scrolling');

        const closeBtn = wrapper.querySelector(CARD_CONFIG.selectors.panelClose);
        if (closeBtn) closeBtn.focus();

        window.log('info', 'ProductCard', 'Size panel opened');
    },

    closePanel(wrapper) {
        wrapper.classList.remove(CARD_CONFIG.classes.panelOpen);
        wrapper.setAttribute('aria-hidden', 'true');
        if (this._activeTrigger) this._activeTrigger.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('stop-scrolling');
        this._activePanel = null;
        this._activeTrigger = null;

        if (this._previousFocus) {
            this._previousFocus.focus();
            this._previousFocus = null;
        }

        window.log('info', 'ProductCard', 'Size panel closed');
    },

    _trapFocus(e, panel) {
        const focusableSelectors = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
        const focusable = Array.from(panel.querySelectorAll(focusableSelectors)).filter((el) => el.offsetParent !== null);
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    },

    // Hide a card's quick-add button once none of its variants have stock left after what's already in the
    // cart — the button's initial visibility is rendered server-side at page load and never reflects cart
    // changes made since (e.g. adding the last unit via this very button).
    async updateQuickAddAvailability() {
        let cartItems;
        try {
            cartItems = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json().then(c => c.items);
        } catch (error) {
            window.log('error', 'ProductCard', 'Failed to fetch cart for stock check', { error: error.message });
            return;
        }

        const cartQtyByVariant = new Map(cartItems.map(item => [String(item.variant_id), item.quantity]));

        document.querySelectorAll(CARD_CONFIG.selectors.productCard).forEach(card => {
            const productData = ProductCardData.getFromCard(card);
            if (!productData) return;

            const button = card.querySelector('.mm-product-card-quick-add');
            if (!button) return;

            const anyVariantPurchasable = productData.variants.some(variant => {
                if (!variant.available) return false;
                const isTracked = variant.inventory_management && variant.inventory_policy !== 'continue';
                if (!isTracked) return true;
                const remainingQty = variant.inventory_quantity - (cartQtyByVariant.get(String(variant.id)) || 0);
                return remainingQty > 0;
            });

            if (!anyVariantPurchasable) {
                button.hidden = true;
            }
        });
    },

    init() {
        // Initialize button states for all quantity inputs
        const quantityInputs = document.querySelectorAll(CARD_CONFIG.selectors.quantityInput);
        quantityInputs.forEach(input => {
            QuantityManager.updateButtonStates(input);
        });

        // Only add event listeners once (use event delegation on document)
        if (!this.initialized) {
            this.setupEventListeners();
            this.initialized = true;
        }
    },

    setupEventListeners() {
        // Event delegation for quantity buttons (MUST be before stopPropagation listener)
        document.addEventListener('click', (e) => {
            // Handle decrease buttons
            const decreaseBtn = e.target.closest(CARD_CONFIG.selectors.quantityDecrease);
            if (decreaseBtn) {
                e.preventDefault();
                e.stopPropagation();
                window.log('info', 'ProductCard', 'Decrease button clicked', { button: decreaseBtn });
                QuantityManager.changeQuantity(-1, decreaseBtn);
                return;
            }

            // Handle increase buttons
            const increaseBtn = e.target.closest(CARD_CONFIG.selectors.quantityIncrease);
            if (increaseBtn) {
                e.preventDefault();
                e.stopPropagation();
                window.log('info', 'ProductCard', 'Increase button clicked', { button: increaseBtn });
                QuantityManager.changeQuantity(1, increaseBtn);
                return;
            }

            // Handle size panel trigger (quick-add for products with variants)
            const panelTrigger = e.target.closest(CARD_CONFIG.selectors.panelTrigger);
            if (panelTrigger) {
                e.preventDefault();
                e.stopPropagation();
                const { wrapper, isNew } = this._mountPanel(panelTrigger.dataset.cardPanelTrigger, panelTrigger);
                if (wrapper) this.openPanel(wrapper, panelTrigger, isNew);
                return;
            }

            // Handle size panel close (close buttons + dark overlay)
            const panelClose = e.target.closest(CARD_CONFIG.selectors.panelClose);
            if (panelClose) {
                e.preventDefault();
                e.stopPropagation();
                const wrapper = panelClose.closest(CARD_CONFIG.selectors.panelWrapper);
                if (wrapper) this.closePanel(wrapper);
                return;
            }
        });

        // Prevent clicks on variant selectors from triggering parent links
        document.addEventListener('click', (e) => {
            const clickedLink = e.target.closest('a');
            if (clickedLink) return;

            const variantLabel = e.target.closest('.mm-card-variant-option');
            if (variantLabel) {
                e.stopPropagation();
            }
        });

        // Escape key closes the active size panel, Tab traps focus inside it
        document.addEventListener('keydown', (e) => {
            if (!this._activePanel) return;

            if (e.key === 'Escape') {
                this.closePanel(this._activePanel);
            } else if (e.key === 'Tab') {
                this._trapFocus(e, this._activePanel);
            }
        });

        // Close the size panel automatically once the item has been added to the cart
        document.addEventListener('cart:add-to-cart-success', () => {
            if (this._activePanel) {
                this.closePanel(this._activePanel);
            }

            this.updateQuickAddAvailability();
        });

        // Event delegation for variant changes
        document.addEventListener('change', (e) => {
            const variantInput = e.target.closest(CARD_CONFIG.selectors.variantInput);
            if (variantInput) {
                // Variant inputs live inside the size panel, which is teleported to <body>
                // (see _mountPanel()) and is therefore not a descendant of the product form.
                const panel = variantInput.closest(CARD_CONFIG.selectors.panelWrapper);
                if (panel) {
                    // Update visual state
                    const optionName = variantInput.name;
                    const allInputsInGroup = panel.querySelectorAll(`input[name="${optionName}"]`);

                    allInputsInGroup.forEach(input => {
                        const label = input.nextElementSibling;
                        if (label && label.tagName === 'LABEL') {
                            if (input.checked) {
                                label.classList.add('mm-selected');
                            } else {
                                label.classList.remove('mm-selected');
                            }
                        }
                    });

                    VariantManager.handleVariantChange(panel);
                } else {
                    window.log('error', 'ProductCard', 'Panel not found for variant input');
                }
            }
        });

        // Event delegation for quantity input changes (sanitize and validate)
        document.addEventListener('input', (e) => {
            const quantityInput = e.target.closest(CARD_CONFIG.selectors.quantityInput);
            if (quantityInput) {
                // Remove non-numeric characters first
                quantityInput.value = quantityInput.value.replace(/[^0-9]/g, '');

                // Then validate and clamp value
                QuantityManager.validateInput(quantityInput);
            }
        });
    }
};

/*********************************************************************************************
 * 🚀 AUTO-INIT
 */
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        ProductCardManager.init();
    });
} else {
    ProductCardManager.init();
}

// Shopify sections: Reinitialize when sections are loaded/reloaded
document.addEventListener('shopify:section:load', (event) => {
    window.log('info', 'ProductCard', 'Shopify section loaded', {
        sectionId: event.detail?.sectionId
    });
    ProductCardManager.init();
});

// Shopify sections: Reinitialize when blocks are loaded
document.addEventListener('shopify:block:select', (event) => {
    window.log('info', 'ProductCard', 'Shopify block selected', {
        blockId: event.detail?.blockId
    });
    ProductCardManager.init();
});

// Export for external use
window.ProductCardManager = ProductCardManager;
